import { RoutesResponse } from "../../../../../../features/calculateRoute";
import { useLocalContent } from "../../../../context";
import { LocalContentProps } from "../../../../types";
import SchoolCatchment from "./SchoolCatchment";
import { POI, POIs, School, Schools } from "@local-logic/client";
import { t } from "@local-logic/core/localization";
import { TrackingEvents, ProductNames } from "@local-logic/core/tracking";
import {
  mapUtils,
  primitiveUtils,
  typeguardUtils,
} from "@local-logic/core/utils";
import { POIMarker } from "@local-logic/elements-react";
import { FeatureCollection, MultiPolygon } from "@turf/turf";
import React, { useEffect, useMemo, useState } from "react";
import { Marker as M } from "react-map-gl";
import { useLangauges, LCTranslationKeys } from "~/context/languageProvider";
import { useStateManagerContext } from "~/context/stateManager";
import { useTracking } from "~/context/trackingProvider";
import { useCalculateRoute } from "~/features/calculateRoute/useCommute";
import { UseMeasureRect } from "~/hooks/useMeasure";

interface POIMarkerProps {
  poi: POI | School;
  poiType: keyof POIs | keyof Schools;
  activePOI: POI | School | undefined;
  hoveredPOI: POI | School | undefined;
  isMobile: boolean;
  isMapMoving: boolean;
  rect: UseMeasureRect;
  walkingTime?: string | null;
  calculateRoute?: () => void;
}

const Marker = ({
  poi,
  poiType,
  activePOI,
  hoveredPOI,
  isMapMoving,
  isMobile,
}: POIMarkerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [schoolCatchment, setSchoolCatchment] =
    useState<FeatureCollection<MultiPolygon>>();
  const isHovered = hoveredPOI === poi;
  const mediumSizedPois: (keyof POIs | keyof Schools)[] = [
    "rapid_transit_stations",
  ];

  const { translations } = useLangauges<LCTranslationKeys>();
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const {
    setCommuteGeojson,
    setDestination,
    setShowCommuteCalculator,
    setIsInputAutoFocused,
    setActivePOI,
  } = useLocalContent();
  const tracking = useTracking();

  const getCommuteData = (commuteMode: string) =>
    useCalculateRoute({
      destination: { lat: poi.lat, lng: poi.lng },
      transportMode: commuteMode,
      origin: `${sdkState?.options?.marker?.lat},${sdkState?.options?.marker?.lng}`,
      isOpen,
    });

  const { data: walkingData } = getCommuteData("pedestrian");

  const { data: drivingData } = getCommuteData("car");

  const [isPoiCalCommute, setIsPoiCalcCommute] = useState(false);

  const minute = t("Minute", {
    _key: "minute",
    _context: "The text that appears in the commute calculator for duration",
    _tags: "local_content_sdk",
    _charlimit: 20,
  });

  const hour = t("Hour", {
    _key: "hour",
    _context: "The text that appears in the commute calculator for duration",
    _tags: "local_content_sdk",
    _charlimit: 20,
  });

  useEffect(() => {
    setIsOpen(activePOI === poi);
    if (activePOI === poi) {
      const activePoiType =
        poiType === "high_schools" || poiType === "primary_schools"
          ? "school"
          : "poi";

      if (
        typeguardUtils.poi.isSchool(poi, activePoiType) &&
        poi?.catchments &&
        Object.keys(poi?.catchments || []).length > 0
      ) {
        setSchoolCatchment(mapUtils.formatGeometry(poi.catchments[0].geometry));
      }
    }
  }, [activePOI, poi]);

  useEffect(() => {
    if (isMapMoving) {
      setSchoolCatchment(undefined);
    }
  }, [isMapMoving]);

  const calculateRoute = () => {
    if (!walkingData?.summary || !isOpen) {
      return;
    }
    setDestination({ label: poi.name, lat: poi.lat, lng: poi.lng });
    setCommuteGeojson(mapUtils.decodePolyline(walkingData.polyline));
    setShowCommuteCalculator(true);
    setIsOpen(false);
    setSchoolCatchment(undefined);
    setIsInputAutoFocused(false);
    setIsPoiCalcCommute(true);
  };

  useEffect(() => {
    if (isPoiCalCommute) {
      tracking.postEvent({
        eventType: TrackingEvents.LOCALCONTENT_COMMUTE_CALCULATE_ROUTE,
        product: ProductNames.LOCAL_CONTENT,
        isMobile,
      });
    }

    return () => {
      setIsPoiCalcCommute(false);
    };
  }, [isPoiCalCommute]);

  const calculateCommuteTime = (commuteData: RoutesResponse | undefined) => {
    if (!commuteData?.summary || !isOpen) {
      return null;
    }

    const hours = Math.floor(commuteData.summary.typicalDuration / 60 / 60);
    const minutes = Math.floor((commuteData.summary.typicalDuration / 60) % 60);

    if (hours === 0) {
      return `${minutes} ${primitiveUtils.string.pluralize(minute, minutes)}`;
    }

    return `${hours} ${primitiveUtils.string.pluralize(
      hour,
      hours
    )} ${minutes} ${primitiveUtils.string.pluralize(minute, minutes)}`;
  };

  const walkingTime = useMemo(
    () => calculateCommuteTime(walkingData),
    [walkingData, isOpen]
  );
  const drivingTime = useMemo(
    () => calculateCommuteTime(drivingData),
    [drivingData, isOpen]
  );

  return (
    <M
      latitude={poi.lat}
      longitude={poi.lng}
      style={{ zIndex: isOpen ? 1 : 0 }}
    >
      <POIMarker.Root
        options={{
          type: poiType as keyof POIs | keyof Schools,
          poi,
          isOpen: isMapMoving ? false : isOpen,
          isHovered,
          side: isMobile ? "bottom" : "right",
          size: mediumSizedPois.includes(poiType as keyof POIs | keyof Schools)
            ? "medium"
            : "small",
          walkingTime,
          drivingTime,
          showDriveTime: !!(
            walkingTime && walkingTime.includes(translations.HOUR)
          ),
          calculateRoute,
        }}
        // eslint-disable-next-line
        onChange={(e: { type: string; data: any }) => {
          if (e.type === "open-change") {
            if (e.data) {
              // setTimeout used to solve issue where switching between
              // POIs would cause the new POI dialog to immediately
              // close.
              setTimeout(() => {
                setActivePOI(poi);
              }, 0);
            } else if (isOpen) {
              setActivePOI(undefined);
              setSchoolCatchment(undefined);
            }
          }
        }}
      >
        <POIMarker.Marker />
        <POIMarker.Content />
      </POIMarker.Root>
      {schoolCatchment && <SchoolCatchment schoolCatchment={schoolCatchment} />}
    </M>
  );
};

export default Marker;
